<script>
    import { toast } from "@zerodevx/svelte-toast";
    import { getAccessToken } from "../auth";
    import { environment } from "../stores";
    import SearchMultiSelect from "./SearchMultiSelect.svelte";
    import { toastThemeError, toastThemeErrorDismissable, toastThemeSuccessDismissable } from "../toasts";
    import Modal from "./Modal.svelte";
    import  ChevronDown from "svelte-material-icons/ChevronDown.svelte";

    let userServiceURL = "https://dev-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    if ($environment === "prod") {
        userServiceURL = "https://ext-prod-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    }

    export let organization;

    let inputError = {
        nameError: false
    };
    let teams = [];
    let teamName = "";
    let users = [];
    let selectedUsers = {};
    let addUsers;
    let teamForDeletion = null;
    let fetchingUsers = false;
    let refreshMultiSelect = false;
    let teamForEdit = null;
    let editUsers = [];
    let selectedEditUsers = {};
    let editName;
    let selectedTeams = new Set;

    $: {
        if (organization) {
            clearData();
            updateTeams(organization._id);
            updateUsers(organization._id);
        }
    }

    async function clearData() {
        selectedUsers = {};
        editUsers = [];
        selectedEditUsers = {};
        addUsers = [];
        teamName = "";
    }

    async function updateTeams(organizationId) {
        const response = await fetchOrganizationTeams(organizationId);
        const data = await response.json();
        if(response.status === 200) {
            teams = data;
        }
        else {
            toast.push(data.message, toastThemeErrorDismissable);
        }
    }

    async function updateUsers(organizationId) {
        if (fetchingUsers){
            return setTimeout(() => updateUsers(organizationId), 100);
        }
        fetchingUsers = true;
        refreshMultiSelect = true;
        const response = await fetchOrganizationsUsers(organizationId);
        const data = await response.json();
        if(response.status === 200) {
            users = data;
        }
        else {
            toast(data.message, toastThemeError);
        }
        fetchingUsers = false;
        refreshMultiSelect = false;
    }

    async function fetchOrganizationTeams(orgId) {
        const authToken = await getAccessToken();
        const response = await fetch(`${userServiceURL}/organization/${orgId}/teams`, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
            },
        });
        return response;
    }

    async function fetchOrganizationsUsers(orgId) {
        const authToken = await getAccessToken();

        const response = await fetch(`${userServiceURL}/organization/${orgId}/users`, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
            },
        });
        return response;
    }

    async function handleEditClick(e, team){
        e.stopPropagation();
        editUsers = team.members.map(member => member._id); 
        team.members.forEach(member => {
            selectedEditUsers[member._id] = {value: member._id, name: "" };
        });
        teamForEdit = team;
    }

    async function editTeam(team) {
        const authToken = await getAccessToken();
        const teamName = editName === "" || editName === undefined ? team.name : editName;
        const body = JSON.stringify({
            name: teamName,
            userIds: editUsers
        });
        const response = await fetch(`${userServiceURL}/team/${team._id}`, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
                "Content-Type": "application/json",
            },
            body: body
        });
        const data = await response.json();
        if (response.status === 200) {
            toast.push(data.message, toastThemeSuccessDismissable);
            updateTeams(organization._id);
        }
        else {
            toast.push(data.message, toastThemeErrorDismissable);
        }
        teamForEdit = null;
    }

    async function removeTeam(team) {
        const authToken = await getAccessToken();
        const response = await fetch(`${userServiceURL}/team/${team._id}`, {
            method: "DELETE",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
            },
        });
        if (response.status === 200) {
            updateTeams(organization._id);
            teamForDeletion = null;
            toast.push("Removed team " + team.name, toastThemeSuccessDismissable);
        }
        return response;
    }

    function remove(value) {
        const { [value]: val, ...rest } = selectedUsers;
        selectedUsers = rest;
    }

    function removeEditUsers(value) {
        const { [value]: val, ...rest } = selectedEditUsers;
        selectedEditUsers = rest;
    }

    async function createTeam() {
        teamName.trim();
        if(!/^(?=.*[\p{L}\p{N}])[\p{L}\p{M}'\s_\-:\d()/]{1,50}$/u.test(teamName)) {
            return inputError.nameError = "Invalid character for name";
        }
        inputError.nameError = false;
        const authToken = await getAccessToken();
        const body = JSON.stringify({
            name: teamName,
            organizationId: organization._id,
            userIds: Object.keys(selectedUsers),
        });
        const response = await fetch(`${userServiceURL}/team`, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
                "Content-Type": "application/json",
            },
            body: body,
        });
        const data = await response.json();
        if (response.status === 200) {
            toast.push(data.message, toastThemeSuccessDismissable);
            teamForEdit == null;
            clearData();
            updateTeams(organization._id);
        }
        else {
            toast.push(data.message, toastThemeErrorDismissable);
        }
    }

    async function resetEdit() {
        editUsers = [];
        selectedEditUsers = {};
        teamForEdit = null;
        editName = "";
    }

    function handleTeam(team) {
        const teamAlreadySelected = selectedTeams.has(team._id);

        if (teamAlreadySelected) {
            selectedTeams.delete(team._id);
            selectedTeams = selectedTeams;
        } else {
            selectedTeams = selectedTeams.add(team._id);
        }
    }

</script>

<main-window>
    <team-body>
        <create-team-card>
            <create-team>
                <left-content>
                    <div>
                        <label for="teamName">Name</label>
                        <input autocomplete="off" name="teamName" bind:value={teamName} />
                        {#if inputError.nameError !== false}
                        <span>{inputError.nameError}</span>
                        {/if}
                    </div>
                    <div>
                        {#if users.length > 0 && !refreshMultiSelect}
                        <label for="users">Users</label>
                        <SearchMultiSelect id={organization._id} bind:selected={selectedUsers} bind:value={addUsers}>
                            {#each users as user}
                            <option value={user._id}>{user.name}</option>
                            {/each}
                        </SearchMultiSelect>
                        {/if}
                    </div>
                </left-content>
                <right-content>
                    <div class="token-container">
                        {#if addUsers && addUsers.length > 0}
                            {#each addUsers as s}
                                <div class="token" data-id={s}>
                                    <span>{users.find((user) => user._id === s).name}</span>
                                    <div class="token-remove" title="Remove {s}" on:click={() => remove(s)}>
                                        <svg
                                            class="icon-clear"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24">
                                            <path
                                                d={"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"} />
                                        </svg>
                                    </div>
                                </div>
                            {/each}
                        {/if}
                    </div>
                </right-content>
            </create-team>
            <button on:click={createTeam}>Create Team</button>
        </create-team-card>
        {#each teams as team}
            <team-card>
                <team-header on:click={() =>handleTeam(team)} on:keypress={() => handleTeam(team)}>
                    <text-header>
                        <span style={selectedTeams.has(team._id) ? "transform: rotateX(180deg)" : "" } on:click|stopPropagation={() => handleTeam(team)} on:keypress={() => handleTeam(team)}>
                            <ChevronDown size={25} on:click={() => handleTeam(team)} />
                        </span>
                        <h3>{team.name}</h3>
                        <p>users: {team.members.length}</p>
                    </text-header>
                    <user-data>
                        <button on:click={(e) => handleEditClick(e, team)}>Edit</button>
                        <button on:click={() => teamForDeletion = team}>Remove</button>
                    </user-data>
                </team-header>
                <team-content style={selectedTeams.has(team._id) ? "max-height:"+ 40*team.members.length + "px" : "max-height:"+ 0}>
                    {#each team.members as member}
                        <user-data>
                            {member.name}
                            <p>({member.email})</p>
                        </user-data>
                    {/each}
                </team-content>
            </team-card>
        {/each}
    </team-body>
    {#if teamForDeletion !== null}
        <Modal on:ok={() => removeTeam(teamForDeletion)} on:close={() => teamForDeletion = null}>
            <h1>Sure you want to remove team?</h1>
            <h2>{teamForDeletion.name}</h2>
        </Modal>
    {/if}
    {#if teamForEdit !== null}
        <Modal on:ok={() => editTeam(teamForEdit)} on:close={() => resetEdit()}>
            <h1>Edit team: {teamForEdit.name}</h1>
            <label for="editName">Name</label>
            <input bind:value={editName} name="editName" placeholder={teamForEdit.name} />
            <label for="newUsers"> Users</label>
            <SearchMultiSelect bind:selected={selectedEditUsers} bind:value={editUsers}>
                {#each users as user}
                    <option value={user._id}>{user.name}</option>
                {/each}
            </SearchMultiSelect>
            <div class="token-container">
                {#if editUsers && editUsers.length}
                    {#each editUsers as s}
                        <div class="token" data-id={s}>
                            <span>{users.find((user) => user._id === s)?.name}</span>
                            <div class="token-remove" title="Remove {s}" on:click={() => removeEditUsers(s)}>
                                <svg
                                    class="icon-clear"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24">
                                    <path
                                        d={"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"} />
                                </svg>
                            </div>
                        </div>
                    {/each}
                {/if}
            </div>
        </Modal>
    {/if}
</main-window>

<style>
    team-body {
        display: flex;
        flex-direction: column;
    }

    create-team-card {
        display: flex;
        flex-direction: column;
        border: solid 2px black;
        padding: 1.5rem 20px;
        margin: 5px 0px;
        align-items: flex-end;
    }

    team-card {
        display: flex;
        flex-direction: column;
        border: solid 2px black;
        padding: 20px 20px;
        margin: 5px 0px;
        align-items: flex-end;
    }

    team-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        cursor: pointer;
    }

    text-header {
        display: flex;
        align-items: center;
    }

    text-header > span {
        transition: ease-in 250ms transform;
        cursor: pointer;
        align-self: center;
        margin-right: 0.25rem;
        margin-bottom: -0.25rem;
    }

    text-header > h3 {
        margin-right: 0.50rem;
    }

    h3, button, p {
        margin:0
    }
    team-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
        justify-content: space-between;
        width: 100%;
        overflow: hidden;
        transition: max-height 200ms ease-out;
    }

    create-team {
        display: flex;
        margin-bottom: 1rem;
        gap: 1rem;
        justify-content: space-between;
        width: 100%;
    }
    left-content {
        flex: 1;
        justify-content: space-around;
        display: flex;
        flex-direction: column;
        max-width: 500px;
    }

    left-content > div {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    left-content > div > span {
        color: red;
        margin-top: -0.25rem;
        margin-left: 0.25rem;
        font-size: 0.85rem;
        text-align: left;
    }


    left-content > div > label {
        text-align: left;
        margin-bottom: 0.25rem;
    }

    right-content {
        flex: 1;
        justify-content: center;
        display: flex;
        max-width: 45%;
    }

    user-data {
        display: flex;
        gap: 1rem;
    }

    user-data > p {
        opacity: 80%;
    }

    .token-container {
        display: flex;
        align-items: flex-start;
        padding: 0 .5rem;
        flex-wrap: wrap;
        background-color: #f9f7f5;
        border: solid 1px #e7e4e2;
        flex: 1;
        min-height: 10rem;
        align-content: flex-start;
    }
    .token {
        align-items: center;
        background-color: hsl(214, 17%, 92%);
        border-radius: 0.5rem;
        display: flex;
        margin: 0.25rem 0.5rem 0.25rem 0;
        max-height: 1.3rem;
        padding: 0.25rem 0.5rem 0.25rem 0.5rem;
        transition: background-color 0.3s;
        white-space: nowrap;
    }
    .token:hover {
        background-color: hsl(214, 15%, 88%);
    }
    .token-remove {
        align-items: center;
        background-color: hsl(214, 15%, 55%);
        border-radius: 50%;
        color: hsl(214, 17%, 92%);
        display: flex;
        justify-content: center;
        height: 1.25rem;
        margin-left: 0.25rem;
        min-width: 1.25rem;
    }
    .token-remove:hover {
        background-color: hsl(215, 21%, 43%);
        cursor: pointer;
    }
</style>
